import React, { useState } from 'react';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useStoreSetup } from '@hooks/useStoreSetup';
import { useAppDispatch, useAppSelector } from '@store';
import { settingActions } from '@store/settings';
import { syncActions, syncThunkActions } from '@store/sync';
import { ThunkActionStatuses } from '@store/types';
import OfflineSyncError from './offline-sync-error';
import { OfflineSyncExecuting } from './offline-sync-executing';
import { OfflineSyncInformation } from './offline-sync-information';
import OfflineSyncSuccess from './offline-sync-success';
import { SyncOfflineVisitDataProps } from '@/store/sync/sync.types';

export type OfflineSyncModalProps = {
  onSubmit: () => void;
  onCancel?: () => void;
  isManual?: boolean;
  avoidNavigation?: boolean;
  generalMessageOveride?: string;
  recommendationTextOveride?: string;
  profileId?: SyncOfflineVisitDataProps; // Used to only sync the saved visit data not the whole app
  applyResetOfAppStore?: boolean;
  applyInitOfStoreSetup?: boolean;
};

const OfflineSyncModal: React.FC<OfflineSyncModalProps> = ({
  onSubmit,
  onCancel,
  isManual = false,
  avoidNavigation = false,
  generalMessageOveride,
  recommendationTextOveride,
  profileId,
  applyResetOfAppStore = true,
  applyInitOfStoreSetup = true,
}) => {
  const { isOnline } = useOnlineStatus();
  const dispatch = useAppDispatch();
  const [unableToSync, setUnableToSync] = useState(false);
  const { initStoreSetup } = useStoreSetup();

  const { status, error, currentAction, currentStep, stepTotal } =
    useAppSelector((state) => state.sync);

  const handleSync = () => {
    dispatch(syncThunkActions.syncOfflineData({}));
    dispatch(settingActions.setLastDataSync());
  };

  const handleOnErrorSubmit = () => {
    setUnableToSync(false);
    dispatch(syncActions.setError(undefined));
    handleSync();
  };

  const handleSyncSuccess = async () => {
    await dispatch(syncActions.clearSyncState());
    // if (applyResetOfAppStore) await resetAppStore();
    if (applyInitOfStoreSetup) await initStoreSetup();
    // close modal LAST
    onSubmit();
  };

  if (status === ThunkActionStatuses.Fulfilled) {
    return (
      <OfflineSyncSuccess onSubmit={handleSyncSuccess}></OfflineSyncSuccess>
    );
  }

  if (unableToSync) {
    return (
      <OfflineSyncError
        onSubmit={handleOnErrorSubmit}
        onCancel={onCancel}
      ></OfflineSyncError>
    );
  }

  if (
    (status === ThunkActionStatuses.Pending && currentAction) ||
    (status === ThunkActionStatuses.Rejected && error)
  ) {
    return (
      <OfflineSyncExecuting
        title={currentAction}
        step={currentStep}
        stepTotal={stepTotal}
        error={error}
        onSyncIssueClick={() => setUnableToSync(true)}
      ></OfflineSyncExecuting>
    );
  }

  return (
    <OfflineSyncInformation
      generalMessageOveride={generalMessageOveride}
      recommendationTextOveride={
        profileId
          ? 'If you choose not to sync now, you will be reminded again when viewing the client profile.'
          : recommendationTextOveride
      }
      isManual={isManual}
      isOnline={isOnline}
      onSubmit={handleSync}
      onCancel={onCancel}
    ></OfflineSyncInformation>
  );
};

export default OfflineSyncModal;
